import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
	Button,
	ButtonContainer,
	ButtonsContainer,
	ExpandableTableRow,
	IconButton,
	IconsContainer,
	LoadingIndicator,
	QueryStateIndicators,
	RelativeContainer,
	SectionHeader,
	TableColumn,
	TableGeneric,
} from "@components";
import { Customer, Division } from "@models";
import { formatDate } from "@service/util";
import Routes from "@service/navigation/routes";
import { useServices, useAsyncCommand, useSortQueryParams } from "@hooks";
import { TableStylesSecondary, Icon } from "@components";
import { Header } from "@components/TableGeneric/TableTypes";
import DivisionRow from "./DivisionRow";
import { colors } from "@theme/theming";
import { StatusToggle, StatusSpan } from "@components/StatusToggle";

type Props = {
	customer: Customer;
	onUpdateCustomer: () => void;
	headers: Header[];
};

export default function CustomerRow(props: Props) {
	const { customer, onUpdateCustomer } = props;
	const { divisionService } = useServices();

	const [fetchDivision, divisionQuery] = useAsyncCommand(async () => {
		return divisionService.getDivisions(customer.customerId);
	});

	const editCustomerUrl = Routes.EditCustomer.makePath({
		customerId: customer.customerId,
	});
	const addDivisionUrl = Routes.AddDivision.makePath({
		customerId: customer.customerId,
	});

	const sortQueryParams = useSortQueryParams();
	const divisionHead: Header[] = [
		{
			title: "Status",
			accessor: "",
			width: 100,
		},
		{
			title: "Name",
			accessor: "",
		},
		{
			title: "Adresse",
			accessor: "",
		},
		{
			title: "Telefon Telefax",
			accessor: "",
		},
		{
			title: "Steuernr",
			accessor: "",
		},
		{
			title: "Umsatzsteuer-Id",
			accessor: "",
		},
		{
			title: "Erstellt",
			accessor: "",
		},
		{
			title: "Geändert",
			accessor: "",
		},
		{
			title: "",
			accessor: "",
		},
	];

	const expandedContent = (
		<td colSpan={props.headers.length} style={{ border: "none" }}>
			{divisionQuery.state === "success" ? (
				<TableStylesSecondary>
					<SectionHeader>
						<h2>Standort</h2>
						<div>
							<ButtonsContainer>
								<StatusToggle
									target="customer"
									targetId={customer.customerId}
									targetName={customer.name}
									curStatus={customer.status}
									onSuccess={onUpdateCustomer}
								/>
								<ButtonContainer>
									<Link to={addDivisionUrl}>
										<Button variant="outline-primary">
											<Icon.Add /> Standort hinzufügen
										</Button>
									</Link>
								</ButtonContainer>
							</ButtonsContainer>
						</div>
					</SectionHeader>

					<TableGeneric
						headers={divisionHead}
						data={divisionQuery.data as Division[]}
						sortOptions={sortQueryParams}
					>
						{(division: Division) => (
							<DivisionRow key={division.divisionId} division={division} />
						)}
					</TableGeneric>
				</TableStylesSecondary>
			) : (
				<RelativeContainer style={{ minHeight: 50 }}>
					<LoadingIndicator />
					<QueryStateIndicators queryState={divisionQuery} absolutePosition />
				</RelativeContainer>
			)}
		</td>
	);

	const handleOnExpand = () => {
		void fetchDivision();
	};

	const row = (
		<>
			<CustomerAccordion
				expandedContent={expandedContent}
				onExpand={handleOnExpand}
			>
				<TableColumn>{customer.customerNo}</TableColumn>
				<TableColumn>
					<StatusSpan status={customer.status} />
				</TableColumn>
				<TableColumn>{customer.name}</TableColumn>
				<TableColumn>
					{customer.created ? formatDate(customer.created) : "-"}
				</TableColumn>
				<TableColumn>
					{customer.updated ? formatDate(customer.updated) : "-"}
				</TableColumn>
				<TableColumn>
					<IconsContainer>
						<Link to={editCustomerUrl}>
							<IconButton tooltip="Kunden bearbeiten">
								<Icon.Edit test-dataid="edit-button" />
							</IconButton>
						</Link>
					</IconsContainer>
				</TableColumn>
			</CustomerAccordion>
		</>
	);

	return row;
}

const CustomerAccordion = styled(ExpandableTableRow)`
	background: ${colors.white} !important;
`;
